import { useQuery } from "@apollo/client";
import { GET_INTERVIEW_SESSION_BASIC } from "../gql";
import type { Conversation } from "@casey/db-types";
import { useMemo } from "react";

interface ConversationBasicData {
  conversation: Conversation | null;
}

interface ConversationBasicVariables {
  conversationId: string;
}

interface UseConversationBasicOptions {
  skip?: boolean;
}

/**
 * Lightweight hook to fetch basic interview session data.
 * Uses the base interview_sessions table (not the view with profiles).
 * Safe for guest users as it doesn't require access to profiles or projects.
 *
 * Use this for auth verification and ownership checks.
 * For full session data with profile info, use useConversationWithProfile.
 */
export function useConversationBasic(
  conversationId: string,
  options?: UseConversationBasicOptions,
) {
  const { data, loading, error, refetch } = useQuery<
    ConversationBasicData,
    ConversationBasicVariables
  >(GET_INTERVIEW_SESSION_BASIC, {
    variables: { conversationId },
    skip: !conversationId || options?.skip,
    errorPolicy: "all",
  });

  const rawSession = data?.conversation || null;

  // Deserialize JSONB context field
  const session = useMemo(() => {
    if (!rawSession) return null;

    const deserialized = { ...rawSession };

    if (typeof rawSession.context === "string") {
      try {
        deserialized.context = JSON.parse(rawSession.context);
      } catch (e) {
        console.error("Failed to parse context:", e);
      }
    }

    return deserialized;
  }, [rawSession]);

  return {
    session,
    loading,
    error: error?.message || null,
    refetch,
  };
}
